import React from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import './DesktopDatePicker.css';
import { createPortal } from 'react-dom';
const RUSSIAN_WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

// Повтор задачи (16.10.2026): раньше ставился только разбором текста.
export const REPEAT_OPTIONS = [
  { code: '', label: 'Не повторять' },
  { code: 'daily', label: 'Каждый день' },
  { code: 'weekdays:mon,tue,wed,thu,fri', label: 'По будням' },
  { code: 'weekly', label: 'Каждую неделю' },
  { code: 'every_N_weeks:2', label: 'Раз в две недели' },
  { code: 'monthly', label: 'Каждый месяц' },
  { code: 'yearly', label: 'Каждый год' },
];

// time — «ЧЧ:ММ» или '' (без времени); при выборе дня время сохраняется.
// onRecurrence — выбрать повтор; нет — блока повтора нет.
export default function DesktopDatePicker({ date, onSelect, onBlur, anchorRef, time = '', recurrence = null, onRecurrence, highlightRepeat = false }) {
  const selected = date ? new Date(date) : undefined;
  const [coords, setCoords] = React.useState(null);
  const [visible, setVisible] = React.useState(false);
  const pickerRef = React.useRef(null);

  // Закрытие при клике за пределами пикера
  React.useEffect(() => {
    function handleClickOutside(e) {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(e.target) &&
        (!anchorRef || !anchorRef.current || !anchorRef.current.contains(e.target))
      ) {
        if (onBlur) onBlur();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [onBlur, anchorRef]);

  // Находим task-row от anchorRef
  function findTaskRow() {
    if (!anchorRef || !anchorRef.current) return null;
    let node = anchorRef.current;
    while (node && node !== document.body) {
      if (node.classList && node.classList.contains('task-row')) return node;
      node = node.parentNode;
    }
    return anchorRef.current; // вне строки списка (карточка «Сейчас») — от самого якоря
  }

  // Рассчитываем позицию по реальным размерам пикера
  function calcPosition(pickerEl) {
    const row = findTaskRow();
    if (!row) return null;

    const rowRect = row.getBoundingClientRect();
    const pickerRect = pickerEl.getBoundingClientRect();
    const pickerHeight = pickerRect.height;
    const pickerWidth = pickerRect.width || 320;
    const gap = 8;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const scrollY = window.scrollY || window.pageYOffset;
    const scrollX = window.scrollX || window.pageXOffset;

    // Вертикаль: вниз, вверх или по центру viewport
    const spaceBelow = viewportHeight - rowRect.bottom;
    const spaceAbove = rowRect.top;

    let top;
    if (spaceBelow >= pickerHeight + gap) {
      top = rowRect.bottom + gap + scrollY;
    } else if (spaceAbove >= pickerHeight + gap) {
      top = rowRect.top - pickerHeight - gap + scrollY;
    } else {
      top = Math.max(gap, (viewportHeight - pickerHeight) / 2) + scrollY;
    }

    // Горизонталь: центр относительно строки, но в пределах viewport
    let left = rowRect.left + (rowRect.width - pickerWidth) / 2 + scrollX;
    left = Math.max(gap + scrollX, Math.min(left, viewportWidth - pickerWidth - gap + scrollX));

    return { top, left };
  }

  // Первый рендер: невидимый пикер за экраном, чтобы замерить реальную высоту
  React.useEffect(() => {
    if (!anchorRef || !anchorRef.current) return;
    // Ставим пикер за экран для замера
    setCoords({ top: -9999, left: -9999 });
    setVisible(false);
  }, [anchorRef]);

  // После рендера невидимого пикера — замеряем и ставим на место
  React.useEffect(() => {
    if (!pickerRef.current || visible || !coords) return;
    // Ждём, пока браузер отрисует пикер за экраном
    const frameId = requestAnimationFrame(() => {
      const pos = calcPosition(pickerRef.current);
      if (pos) {
        setCoords(pos);
        setVisible(true);
      }
    });
    return () => cancelAnimationFrame(frameId);
  });




  function handleSelect(day) {
    if (day) {
      const offset = day.getTimezoneOffset() * 60000;
      const formatted = new Date(day.getTime() - offset)
        .toISOString()
        .split('T')[0];
      onSelect(time ? `${formatted}T${time}` : formatted);
    }
    if (onBlur) onBlur();
  }

  function todayKey() {
    const d = new Date();
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().split('T')[0];
  }

  // Время без даты — на сегодня. Пусто — убрать время, дата остаётся.
  function handleTime(value) {
    const day = date || todayKey();
    onSelect(value ? `${day}T${value}` : day);
  }

  function handleQuickDate(daysFromNow) {
    const d = new Date();
    d.setDate(d.getDate() + daysFromNow);
    const offset = d.getTimezoneOffset() * 60000;
    const formatted = new Date(d.getTime() - offset)
      .toISOString()
      .split('T')[0];
    onSelect(time ? `${formatted}T${time}` : formatted);
    if (onBlur) onBlur();
  }

  function handleClear() {
    onSelect('');
    if (onBlur) onBlur();
  }

  if (!coords) return null;

  return createPortal(
          <div
            ref={pickerRef}
            className="desktop-date-picker"
            style={{
              position: 'absolute',
              top: coords.top,
              left: coords.left,
              zIndex: 9999,
              width: 320,
              minWidth: 180,
              visibility: visible ? 'visible' : 'hidden',
            }}
          >
            <div className="date-quick-buttons">
              <button className="date-quick-btn" onClick={() => handleQuickDate(1)}>Завтра</button>
              <button className="date-quick-btn" onClick={() => handleQuickDate(2)}>Послезавтра</button>
              <button className="date-quick-btn" onClick={() => handleQuickDate(7)}>Через неделю</button>
            </div>
            <DayPicker
              mode="single"
              weekStartsOn={1}
              formatters={{
                formatWeekdayName: (day) => RUSSIAN_WEEKDAYS[(day.getDay() + 6) % 7]
              }}
              selected={selected}
              onSelect={handleSelect}
            />
            <div className="date-extra">
              <label className="date-extra-row">
                <span>Время</span>
                <input
                  type="time"
                  className="date-extra-input"
                  value={time}
                  onChange={e => handleTime(e.target.value)}
                />
              </label>
              {onRecurrence && (
                <label className={`date-extra-row ${highlightRepeat ? 'is-highlight' : ''}`}>
                  <span>Повтор</span>
                  <select
                    className="date-extra-input"
                    value={REPEAT_OPTIONS.some(o => o.code === (recurrence || '')) ? (recurrence || '') : 'custom'}
                    onChange={e => onRecurrence(e.target.value || null)}
                  >
                    {!REPEAT_OPTIONS.some(o => o.code === (recurrence || '')) && <option value="custom" disabled>Свой повтор</option>}
                    {REPEAT_OPTIONS.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
                  </select>
                </label>
              )}
            </div>
            {date && (
              <button className="date-picker-clear" onClick={handleClear}>
                Без даты
              </button>
            )}
          </div>,
          document.body
        );
}
